import {
  createFileRoute,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { useAuth } from '../contexts/AuthContext';
import {
  useLegacyAuthGatewayRq,
  useLegacyAuthGatewayFetch,
} from '@penta/penta-api';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner, Modal } from '@penta/penta-shared';
import styles from './login.module.scss';

export const Route = createFileRoute('/login')({
  component: Login,
});

export function Login() {
  const { t } = useTranslation();
  const { login: setToken, isAuthenticated } = useAuth();
  const legacyApi = useLegacyAuthGatewayRq();
  const loginMutation = legacyApi.useMutation('post', '/v1/token');
  const fetchClient = useLegacyAuthGatewayFetch();
  const navigate = useNavigate();
  const location = useLocation(); // location object

  // Track processed auth code to prevent double execution in Strict Mode or re-renders
  // Initialize as undefined to distinguish from "no code" (null)
  const processedCodeRef = useRef<string | null | undefined>(undefined);
  const loginCompletedRef = useRef(false);

  const [status, setStatus] = useState<string>(t('login.loading.initializing'));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleLogin = async () => {
      try {
        if (loginCompletedRef.current) {
          return;
        }

        if (isAuthenticated) {
          loginCompletedRef.current = true;
          navigate({ to: '/' });
          return;
        }

        // Parse code from hash #code=xyz
        // Prioritize router location, fallback to window for robustness
        const hashStr = location.hash || window.location.hash || '';

        const hashParams = new URLSearchParams(hashStr.replace(/^#/, ''));
        const code = hashParams.get('code');

        console.log('[Login] Processing hash:', {
          hashStr,
          code,
          previousCode: processedCodeRef.current,
        });

        // Skip if we already processed this exact code (or lack thereof)
        // This prevents double-execution in Strict Mode and redirect loops
        if (processedCodeRef.current === code) {
          console.log('[Login] Skipping duplicate execution for code:', code);
          return;
        }
        processedCodeRef.current = code;

        if (code) {
          setStatus(t('login.loading.verifying'));

          try {
            const data = await loginMutation.mutateAsync({ body: { code } });

            if (data && data.token) {
              loginCompletedRef.current = true;
              setToken(data.token);
              navigate({ to: '/' });
            } else {
              setError(
                t('login.error.failedPrefix', {
                  message: t('login.error.validationFailed'),
                }),
              );
            }
          } catch (apiError) {
            console.error('Login failed', apiError);
            const msg =
              (apiError as { message?: string }).message ||
              t('login.error.validationFailed');
            setError(t('login.error.failedPrefix', { message: msg }));
          }
        } else {
          setStatus(t('login.loading.connecting'));

          // Call API without code to get redirect URL
          // We expect 401, so we check response status. We keep fetchClient here
          // because we need direct access to the response headers for the 401 redirect mechanic.
          const { response } = await fetchClient.POST('/v1/token', {
            body: { code: '' },
          });

          if (response.status === 401) {
            const authHeader = response.headers.get('WWW-Authenticate');
            if (authHeader) {
              // Parse "Url https://..."
              // Example: Url https://ibankdev.privatbanka.sk/ExtranetRedirect/Pages/Login.aspx
              const match = authHeader.match(/^Url\s+(.+)$/i);
              if (match && match[1]) {
                const redirectUrl = match[1];
                console.log('Redirecting to:', redirectUrl);
                window.location.href = redirectUrl;
                return;
              }
            }
            console.error(
              'Missing or invalid WWW-Authenticate header',
              authHeader,
            );
            setError(t('login.error.invalidHeader'));
          } else {
            setError(
              t('login.error.unexpectedStatus', { status: response.status }),
            );
          }
        }
      } catch (err: unknown) {
        console.error(err);
        setError(err instanceof Error ? err.message : t('login.error.unknown'));
      }
    };

    handleLogin();
    // Re-run if hash changes
  }, [location.hash, fetchClient, setToken, navigate, t, isAuthenticated]);

  if (error) {
    return (
      <Modal
        open
        onOpenChange={() => {
          processedCodeRef.current = undefined;
          window.location.reload();
        }}
        title={t('login.error.title')}
        description={error}
        data-testid="login-error-modal"
      >
        <button
          onClick={() => {
            processedCodeRef.current = undefined;
            window.location.reload();
          }}
          className={styles['retryButton']}
        >
          {t('login.retry')}
        </button>
      </Modal>
    );
  }

  return (
    <Modal
      open
      closeOnInteractOutside={false}
      closeOnEscape={false}
      aria-label={status}
      data-testid="login-loading"
    >
      <Spinner size="lg" />
    </Modal>
  );
}
